import type { CaptureResult } from "posthog-js";

const DYNAMIC_PATH_RULES = [
  {
    pattern: /^\/x\/inbox\/[^/?#]+(?=\/|$)/,
    replacement: "/x/inbox/:chatId",
  },
  {
    pattern: /^\/users\/[^/?#]+(?=\/|$)/,
    replacement: "/users/:username",
  },
] as const;

/**
 * App Router pages with dynamic segments. Error telemetry uses these concrete
 * route templates to preserve route shape without sending any dynamic value.
 * Ordinary pageview analytics still uses the narrower rules above so useful
 * public dimensions such as game, set, format, city, and store remain intact.
 *
 * `posthog-path-sanitizer.test.mjs` derives the same set from `src/app`, so a
 * new dynamic page cannot silently bypass this boundary.
 */
export const ERROR_DOCUMENT_ROUTE_TEMPLATES = [
  "/city/[citySlug]",
  "/city/[citySlug]/haves",
  "/city/[citySlug]/locked",
  "/city/[citySlug]/matched",
  "/city/[citySlug]/wants",
  "/decks/[game]",
  "/decks/[game]/[slug]",
  "/decks/[game]/[slug]/[deck_id]",
  "/drops/[dropId]",
  "/help/[section]",
  "/home-variant/[locationKey]/[gamesKey]",
  "/lists/[list_id]",
  "/metagame/[format]",
  "/metagame/[format]/[archetype_slug]",
  "/metagame/[format]/[archetype_slug]/[deck_id]",
  "/r/[code]",
  "/scanner/[game]",
  "/sets/[game]",
  "/sets/[game]/[set_code]",
  "/sets/[game]/[set_code]/[card_name]/[card_number]",
  "/sets/[game]/[set_code]/[card_name]/[card_number]/[printing]",
  "/sets/[game]/[set_code]/sealed",
  "/sets/[game]/[set_code]/sealed/[slug]",
  "/sign-in/[[...rest]]",
  "/sign-up/[[...rest]]",
  "/stores/[slug]",
  "/stores/[slug]/haves",
  "/stores/[slug]/matched",
  "/stores/[slug]/traders",
  "/stores/[slug]/wants",
  "/users/[username]",
  "/users/[username]/haves",
  "/users/[username]/lists/[list_id]",
  "/users/[username]/matched",
  "/users/[username]/wants",
  // This legacy redirect-only route still needs error redaction, but keep its
  // value compositional so navigation guards do not mistake metadata for a
  // live Collection destination.
  ["", "x", "collection", "import", "[id]"].join("/"),
  "/x/decks/[game]",
  "/x/decks/[game]/[deck_id]",
  "/x/inbox/[chatId]",
  "/x/inbox/[chatId]/proposal-picker",
  "/x/lists/[list_id]",
  "/x/lists/[list_id]/import",
  "/x/lists/[list_id]/import/[id]",
  "/x/lists/[list_id]/import/history",
  "/x/settings/[[...user-profile]]",
  "/x/stores/[slug]",
  "/x/stores/[slug]/haves",
  "/x/stores/[slug]/matched",
  "/x/stores/[slug]/wants",
] as const;

// Exception telemetry fails closed for an unmatched route. Preserve only
// public, non-identifying top-level route names; unknown nested values collapse
// to the family plus `:unmatched` until a concrete App Router template is
// added above (the filesystem-derived test enforces that for every dynamic
// page).
const SAFE_ERROR_TOP_LEVEL_PATHS = new Set([
  "about",
  "city",
  "contact",
  "decks",
  "drops",
  "forbidden",
  "help",
  "lists",
  "metagame",
  "privacy",
  "r",
  "scanner",
  "sealed-products",
  "sets",
  "sign-in",
  "sign-up",
  "stores",
  "users",
  "x",
]);

type SanitizedRouteMatch = {
  consumedSegments: number;
  literalSegments: number;
  segments: string[];
};

function dynamicSegmentName(segment: string): string | undefined {
  return /^\[([^.[\]]+)\]$/.exec(segment)?.[1];
}

function catchAllSegmentName(segment: string): string | undefined {
  return /^\[\[?\.\.\.([^\]]+)\]\]?$/.exec(segment)?.[1];
}

function sanitizeUsingRouteTemplate(
  pathnameSegments: readonly string[],
  template: string
): SanitizedRouteMatch | undefined {
  const templateSegments = template.slice(1).split("/");
  const sanitizedSegments = [...pathnameSegments];
  let literalSegments = 0;
  let pathnameIndex = 0;

  for (const templateSegment of templateSegments) {
    const catchAllName = catchAllSegmentName(templateSegment);
    if (catchAllName) {
      const consumed = pathnameSegments.length - pathnameIndex;
      if (!templateSegment.startsWith("[[") && consumed === 0) {
        return undefined;
      }
      if (consumed > 0) {
        sanitizedSegments.splice(pathnameIndex, consumed, `:${catchAllName}`);
      }
      return {
        consumedSegments: pathnameSegments.length,
        literalSegments,
        segments: sanitizedSegments,
      };
    }

    const pathnameSegment = pathnameSegments[pathnameIndex];
    if (!pathnameSegment) {
      return undefined;
    }

    const dynamicName = dynamicSegmentName(templateSegment);
    if (dynamicName) {
      sanitizedSegments[pathnameIndex] = `:${dynamicName}`;
    } else if (pathnameSegment === templateSegment) {
      literalSegments += 1;
    } else {
      return undefined;
    }
    pathnameIndex += 1;
  }

  // A neighboring shorter route must not partially sanitize an unknown
  // suffix and leave the remaining private value intact.
  if (pathnameIndex !== pathnameSegments.length) {
    return undefined;
  }

  return {
    consumedSegments: pathnameIndex,
    literalSegments,
    segments: sanitizedSegments,
  };
}

const URL_PROPERTY_NAMES = [
  "$current_url",
  "$referrer",
  "$initial_current_url",
  "$initial_referrer",
] as const;

const PATH_PROPERTY_NAMES = [
  "$pathname",
  "$prev_pageview_pathname",
  "$initial_pathname",
  "document_pathname",
] as const;

function errorDocumentPathnameMatch(pathname: string): string | undefined {
  const suffixIndex = pathname.search(/[?#]/);
  const path = suffixIndex === -1 ? pathname : pathname.slice(0, suffixIndex);
  const suffix = suffixIndex === -1 ? "" : pathname.slice(suffixIndex);
  if (!path.startsWith("/")) {
    return undefined;
  }

  const trailingSlash = path.length > 1 && path.endsWith("/");
  const pathnameSegments = path
    .slice(1, trailingSlash ? -1 : undefined)
    .split("/");
  let bestMatch: SanitizedRouteMatch | undefined;

  for (const template of ERROR_DOCUMENT_ROUTE_TEMPLATES) {
    const match = sanitizeUsingRouteTemplate(pathnameSegments, template);
    if (
      match &&
      (!bestMatch ||
        match.consumedSegments > bestMatch.consumedSegments ||
        (match.consumedSegments === bestMatch.consumedSegments &&
          match.literalSegments > bestMatch.literalSegments))
    ) {
      bestMatch = match;
    }
  }

  if (!bestMatch) {
    return undefined;
  }

  return `/${bestMatch.segments.join("/")}${trailingSlash ? "/" : ""}${suffix}`;
}

export function cleanErrorDocumentPathname(pathname: string): string {
  return errorDocumentPathnameMatch(pathname) ?? pathname;
}

export function cleanAnalyticsPathname(pathname: string): string {
  return DYNAMIC_PATH_RULES.reduce(
    (cleanedPathname, rule) =>
      cleanedPathname.replace(rule.pattern, rule.replacement),
    pathname
  );
}

function cleanAnalyticsUrl(value: string): string {
  try {
    const url = new URL(value);
    url.pathname = cleanAnalyticsPathname(url.pathname);
    return url.toString();
  } catch {
    return cleanAnalyticsPathname(value);
  }
}

const DEPLOYMENT_ID_PATTERN = /^[A-Za-z0-9_-]{1,160}$/;
const ABSOLUTE_URL_IN_TEXT_PATTERN = /https?:\/\/[^\s"'<>]+/gi;
const STATIC_ASSET_IN_TEXT_PATTERN =
  /\/_next\/static\/(?:chunks|css)\/[A-Za-z0-9_./-]+\.(?:js|css)(?:\?[^\s"'<>)]*)?/gi;

function cleanExceptionPathname(value: string): string {
  const pathname = value.split(/[?#]/, 1)[0] || "/";
  const matched = errorDocumentPathnameMatch(pathname);
  if (matched) {
    return matched;
  }

  const segments = pathname.split("/").filter(Boolean);
  if (segments.length === 0) {
    return "/";
  }
  const family = segments[0];
  if (!SAFE_ERROR_TOP_LEVEL_PATHS.has(family)) {
    return "/:unmatched";
  }
  return segments.length === 1 ? `/${family}` : `/${family}/:unmatched`;
}

function cleanExceptionUrl(value: string): string {
  const isRelative = value.startsWith("/");
  try {
    const url = new URL(value, "https://asset.invalid");
    const isStaticAsset = url.pathname.startsWith("/_next/static/");
    if (!isStaticAsset) {
      url.pathname = cleanExceptionPathname(url.pathname);
    }
    const deploymentId = url.searchParams.get("dpl") || "";
    url.search = "";
    url.hash = "";
    if (isStaticAsset && DEPLOYMENT_ID_PATTERN.test(deploymentId)) {
      url.searchParams.set("dpl", deploymentId);
    }
    return isRelative ? `${url.pathname}${url.search}` : url.toString();
  } catch {
    return cleanExceptionPathname(value);
  }
}

function cleanExceptionText(value: string): string {
  const cleanAbsoluteUrl = (raw: string) => {
    const trailing = /[),.;\]]+$/.exec(raw)?.[0] || "";
    const url = trailing ? raw.slice(0, -trailing.length) : raw;
    return `${cleanExceptionUrl(url)}${trailing}`;
  };

  return value
    .replace(ABSOLUTE_URL_IN_TEXT_PATTERN, cleanAbsoluteUrl)
    .replace(STATIC_ASSET_IN_TEXT_PATTERN, (asset) => cleanExceptionUrl(asset));
}

function sanitizeExceptionList(value: unknown): unknown {
  if (!Array.isArray(value)) {
    return value;
  }

  return value.map((candidate) => {
    if (!candidate || typeof candidate !== "object") {
      return candidate;
    }
    const exception = { ...(candidate as Record<string, unknown>) };
    if (typeof exception.value === "string") {
      exception.value = cleanExceptionText(exception.value);
    }

    if (exception.stacktrace && typeof exception.stacktrace === "object") {
      const stacktrace = {
        ...(exception.stacktrace as Record<string, unknown>),
      };
      if (Array.isArray(stacktrace.frames)) {
        stacktrace.frames = stacktrace.frames.map((candidateFrame) => {
          if (!candidateFrame || typeof candidateFrame !== "object") {
            return candidateFrame;
          }
          const frame = { ...(candidateFrame as Record<string, unknown>) };
          for (const propertyName of ["filename", "abs_path"]) {
            if (typeof frame[propertyName] === "string") {
              frame[propertyName] = cleanExceptionText(frame[propertyName]);
            }
          }
          return frame;
        });
      }
      exception.stacktrace = stacktrace;
    }
    return exception;
  });
}

function sanitizeExceptionProperties(
  properties: CaptureResult["properties"] | undefined
): CaptureResult["properties"] | undefined {
  if (!properties) {
    return properties;
  }
  const sanitizedProperties = { ...properties };

  for (const propertyName of [
    "original_message",
    "$exception_message",
    "chunk_failed_asset",
  ]) {
    const value = sanitizedProperties[propertyName];
    if (typeof value === "string") {
      sanitizedProperties[propertyName] = cleanExceptionText(value);
    }
  }
  if (Array.isArray(sanitizedProperties.$exception_values)) {
    sanitizedProperties.$exception_values =
      sanitizedProperties.$exception_values.map((value) =>
        typeof value === "string" ? cleanExceptionText(value) : value
      );
  }
  sanitizedProperties.$exception_list = sanitizeExceptionList(
    sanitizedProperties.$exception_list
  ) as CaptureResult["properties"]["$exception_list"];
  return sanitizedProperties;
}

function sanitizeLocationProperties(
  properties: CaptureResult["properties"] | undefined,
  exceptionEvent = false
): CaptureResult["properties"] | undefined {
  if (!properties) {
    return properties;
  }

  const sanitizedProperties = { ...properties };

  for (const propertyName of URL_PROPERTY_NAMES) {
    const value = sanitizedProperties[propertyName];
    if (typeof value === "string") {
      sanitizedProperties[propertyName] = exceptionEvent
        ? cleanExceptionUrl(value)
        : cleanAnalyticsUrl(value);
    }
  }

  for (const propertyName of PATH_PROPERTY_NAMES) {
    const value = sanitizedProperties[propertyName];
    if (typeof value === "string") {
      sanitizedProperties[propertyName] = exceptionEvent
        ? cleanExceptionPathname(value)
        : cleanAnalyticsPathname(value);
    }
  }

  return sanitizedProperties;
}

/**
 * React's <ViewTransition> ignores the hidden-document skip by exact message,
 * but only knows older Chrome's wording. Firefox's, and newer Chromium's (which
 * appends ". Document hidden"), fall through to onRecoverableError ->
 * window.reportError -> exception autocapture, though React still commits the
 * update, so nothing is broken. PostHog's DOMException coercer records
 * `type: "DOMException"` with the name prefixed to the value.
 */
// ponytail: drop once upstream React's startViewTransition matches these texts
const BENIGN_VIEW_TRANSITION_SKIPS = new Set([
  "InvalidStateError: Skipped ViewTransition due to document being hidden",
  "InvalidStateError: Transition was aborted because of invalid state. Document hidden",
]);

function isBenignViewTransitionSkip(
  properties: CaptureResult["properties"] | undefined
): boolean {
  const list = properties?.$exception_list;
  return (
    Array.isArray(list) &&
    list.some((exception) => BENIGN_VIEW_TRANSITION_SKIPS.has(exception?.value))
  );
}

/** Removes dynamic route values before an event leaves the browser. */
export function sanitizeAnalyticsEvent(
  event: CaptureResult | null
): CaptureResult | null {
  if (!event) {
    return null;
  }

  const exceptionEvent = event.event === "$exception";
  if (exceptionEvent && isBenignViewTransitionSkip(event.properties)) {
    return null;
  }
  const properties = sanitizeLocationProperties(
    event.properties,
    exceptionEvent
  );

  return {
    ...event,
    properties:
      (exceptionEvent ? sanitizeExceptionProperties(properties) : properties) ??
      {},
    $set: sanitizeLocationProperties(event.$set, exceptionEvent),
    $set_once: sanitizeLocationProperties(event.$set_once, exceptionEvent),
  };
}
